import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { emailQueueAPI, emailAccountsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Inbox,
  Mail,
  Check,
  X,
  AlertTriangle,
  Paperclip,
  RefreshCw,
  Loader2,
  Settings,
  FileText,
  Download,
  File,
  RotateCcw,
} from "lucide-react";

export function EmailQueuePage() {
  const { isAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [message, setMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [accountFilter, setAccountFilter] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const navigate = useNavigate();

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter, accountFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [queueRes, accountsRes] = await Promise.all([
        emailQueueAPI.getAll({ status: statusFilter || undefined, account_id: accountFilter || undefined }),
        emailAccountsAPI.getAll(),
      ]);
      setEmails(queueRes.data.items);
      setPendingCount(queueRes.data.pending_count);
      setAccounts(accountsRes.data);
    } catch (error) {
      showMessage("Errore nel caricamento", "error");
    } finally {
      setLoading(false);
    }
  };

  const syncAllAccounts = async () => {
    if (accounts.length === 0) {
      showMessage("Nessun account configurato", "error");
      return;
    }
    
    setSyncing(true);
    let totalNew = 0;
    
    for (const account of accounts) {
      if (account.is_active) {
        try {
          const response = await emailAccountsAPI.sync(account.id);
          const match = response.data.message.match(/(\d+)/);
          if (match) totalNew += parseInt(match[1]);
        } catch (error) {
          // Continue with other accounts
        }
      }
    }
    
    showMessage(`Sincronizzazione completata. ${totalNew} nuove email.`);
    setSyncing(false);
    loadData();
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      const response = await emailQueueAPI.approve(id);
      showMessage(`Protocollato: ${response.data.protocol_number}`);
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await emailQueueAPI.reject(id);
      showMessage("Email ignorata");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setProcessing(null);
    }
  };

  const handleSpam = async (id) => {
    setProcessing(id);
    try {
      await emailQueueAPI.markSpam(id);
      showMessage("Segnata come spam");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setProcessing(null);
    }
  };

  const handleRestore = async (id) => {
    setProcessing(id);
    try {
      await emailQueueAPI.restore(id);
      showMessage("Email ripristinata in coda");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setProcessing(null);
    }
  };

  const handleDownloadAttachment = async (emailId, attachmentIndex, filename) => {
    try {
      const response = await emailQueueAPI.downloadAttachment(emailId, attachmentIndex);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showMessage("Errore nel download dell'allegato", "error");
    }
  };

  const clearAllEmails = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare TUTTE le email dalla coda? Questa azione non è reversibile.")) return;
    try {
      await emailQueueAPI.clearAll();
      showMessage("Coda email svuotata");
      loadData();
    } catch (error) {
      showMessage("Errore durante la cancellazione", "error");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">In attesa</Badge>;
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Protocollata</Badge>;
      case "rejected":
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Ignorata</Badge>;
      case "spam":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Spam</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Inbox size={32} className="text-[#2D7BB7]" />
            Email
          </h1>
          <p className="text-muted-foreground mt-1">
            {pendingCount > 0 ? (
              <span className="text-amber-600 font-medium">{pendingCount} email in attesa di revisione</span>
            ) : (
              "Nessuna email in attesa"
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin() && (
            <Link to="/settings/email-accounts">
              <Button variant="outline" className="border-border text-foreground">
                <Settings className="mr-2" size={16} />
                Account
              </Button>
            </Link>
          )}
          {isAdmin() && emails.length > 0 && (
            <Button
              onClick={clearAllEmails}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="clear-all-emails-btn"
            >
              <X className="mr-2" size={16} />
              Svuota coda
            </Button>
          )}
          <Button
            onClick={syncAllAccounts}
            disabled={syncing || accounts.length === 0}
            className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
            data-testid="sync-all-btn"
          >
            {syncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2" size={16} />
            )}
            Sincronizza
          </Button>
        </div>
      </div>

      {/* No accounts warning - only for admin */}
      {isAdmin() && accounts.length === 0 && !loading && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={24} className="text-amber-600" />
              <div>
                <p className="text-amber-800 font-medium">Nessun account email configurato</p>
                <p className="text-amber-700 text-sm">
                  <Link to="/settings/email-accounts" className="underline hover:no-underline">
                    Configura un account
                  </Link> per iniziare a ricevere email.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Stato:</span>
              <Select
                value={statusFilter || undefined}
                onValueChange={(val) => setStatusFilter(val === "__all__" ? "" : val)}
              >
                <SelectTrigger className="w-[160px] bg-background border-input" data-testid="status-filter">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti</SelectItem>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="approved">Protocollate</SelectItem>
                  <SelectItem value="rejected">Ignorate</SelectItem>
                  <SelectItem value="spam">Spam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Account:</span>
              <Select
                value={accountFilter || undefined}
                onValueChange={(val) => setAccountFilter(val === "__all__" ? "" : val)}
              >
                <SelectTrigger className="w-[250px] bg-background border-input" data-testid="account-filter">
                  <SelectValue placeholder="Tutti gli account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti gli account</SelectItem>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <Mail size={20} className="text-[#2D7BB7]" />
            Email ({emails.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32 bg-muted rounded-lg" />
              ))}
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Inbox size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessuna email trovata</p>
              {statusFilter === "pending" && accounts.length > 0 && (
                <p className="text-sm mt-2">Clicca "Sincronizza" per scaricare nuove email</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className={`p-4 rounded-lg border ${
                    email.status === "pending" 
                      ? "bg-amber-50/50 border-amber-200" 
                      : "bg-muted/50 border-border"
                  }`}
                  data-testid={`email-item-${email.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(email.status)}
                        <Badge className="bg-muted text-muted-foreground border-border">
                          {email.account_name}
                        </Badge>
                        {email.has_attachments && (
                          <Badge 
                            className="bg-blue-100 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-200"
                            onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                          >
                            <Paperclip size={12} className="mr-1" />
                            {email.attachment_count} allegat{email.attachment_count === 1 ? "o" : "i"}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Subject */}
                      <h3 className="text-foreground font-medium truncate mb-1">
                        {email.subject}
                      </h3>
                      
                      {/* Sender & Date */}
                      <p className="text-sm text-muted-foreground mb-2">
                        Da: <span className="text-foreground">{email.sender}</span>
                        {" • "}
                        {formatDate(email.date)}
                      </p>
                      
                      {/* Body Preview */}
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {email.body_preview}
                      </p>

                      {/* Attachments - Expandable */}
                      {email.has_attachments && expandedEmail === email.id && email.attachments && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs font-medium text-blue-800 mb-2">Allegati:</p>
                          <div className="space-y-2">
                            {email.attachments.map((att, index) => (
                              <div 
                                key={index}
                                className="flex items-center justify-between p-2 bg-white rounded border border-blue-100"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <File size={16} className="text-blue-600 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm text-foreground truncate">{att.filename}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatFileSize(att.size)}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAttachment(email.id, index, att.filename)}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 flex-shrink-0"
                                >
                                  <Download size={14} className="mr-1" />
                                  Scarica
                                </Button>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-blue-700 mt-2">
                            Gli allegati verranno automaticamente inclusi nel protocollo
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    {email.status === "pending" && (
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/protocols/new?from_email=${email.id}`)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`approve-${email.id}`}
                        >
                          <Check size={14} className="mr-1" />
                          Protocolla
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(email.id)}
                          disabled={processing === email.id}
                          className="border-border text-foreground"
                          data-testid={`reject-${email.id}`}
                        >
                          <X size={14} className="mr-1" />
                          Ignora
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSpam(email.id)}
                          disabled={processing === email.id}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`spam-${email.id}`}
                        >
                          <AlertTriangle size={14} className="mr-1" />
                          Spam
                        </Button>
                      </div>
                    )}
                    
                    {/* Show protocol number if approved */}
                    {email.status === "approved" && (
                      <div className="flex flex-col items-end gap-2">
                        {email.protocol_number && (
                          <Link 
                            to={`/protocols/${email.protocol_id}`}
                            className="flex items-center gap-2 text-[#2D7BB7] hover:underline"
                          >
                            <FileText size={16} />
                            {email.protocol_number}
                          </Link>
                        )}
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestore(email.id)}
                            disabled={processing === email.id}
                            className="border-amber-200 text-amber-600 hover:bg-amber-50"
                            data-testid={`restore-${email.id}`}
                          >
                            {processing === email.id ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <RotateCcw size={14} className="mr-1" />
                            )}
                            Ripristina
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Restore button for rejected/spam emails */}
                    {(email.status === "rejected" || email.status === "spam") && isAdmin() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore(email.id)}
                        disabled={processing === email.id}
                        className="border-amber-200 text-amber-600 hover:bg-amber-50"
                        data-testid={`restore-${email.id}`}
                      >
                        {processing === email.id ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <RotateCcw size={14} className="mr-1" />
                        )}
                        Ripristina
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
